import React, { useEffect, useState } from 'react';
import { motion, useMotionValue, useTransform, animate } from 'framer-motion';
import { CheckIcon, XIcon } from 'lucide-react';
import { ColumviLogo } from './ColumviLogo';
import { ColumviBackground } from './ColumviBackground';
interface RewardSlideProps {
  isCorrect: boolean;
  selectedAnswer: string;
  onContinue: () => void;
}
export function RewardSlide({
  isCorrect,
  selectedAnswer,
  onContinue
}: RewardSlideProps) {
  const peerPercentage = isCorrect ? 74 : 26;
  // Animated counter for 42%
  const count = useMotionValue(0);
  const rounded = useTransform(count, v => Math.round(v));
  const [displayCount, setDisplayCount] = useState(0);
  useEffect(() => {
    const controls = animate(count, 42, {
      duration: 2,
      delay: 0.8,
      ease: 'easeOut'
    });
    const unsubscribe = rounded.on('change', v => setDisplayCount(v));
    return () => {
      controls.stop();
      unsubscribe();
    };
  }, []);
  return <ColumviBackground showWave={false} data-id="element-179">
      <div className="flex flex-col h-full w-full p-6 relative overflow-y-auto" data-id="element-180">
        {/* Header: Logo + Feedback Pill */}
        <div className="flex items-center justify-between mb-6 mt-2" data-id="element-181">
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.5
        }} data-id="element-182">
            <ColumviLogo size="sm" variant="light" data-id="element-183" />
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.4,
          delay: 0.2
        }} data-id="element-184">
            {isCorrect ? <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-500/20 border border-green-400/30 backdrop-blur-sm" data-id="element-185">
                <CheckIcon className="w-3.5 h-3.5 text-green-300" data-id="element-186" />
                <span className="text-green-200 text-xs font-medium" style={{
              fontFamily: 'var(--font-gotham)'
            }} data-id="element-187">
                  Correct
                </span>
              </div> : <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/20 border border-amber-400/30 backdrop-blur-sm" data-id="element-188">
                <XIcon className="w-3.5 h-3.5 text-amber-300" data-id="element-189" />
                <span className="text-amber-200 text-xs font-medium" style={{
              fontFamily: 'var(--font-gotham)'
            }} data-id="element-190">
                  Not quite
                </span>
              </div>}
          </motion.div>
        </div>

        {/* Peer Poll */}
        <motion.div className="mb-6" initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} data-id="element-191">
          <p className="text-white/70 text-sm mb-2" style={{
          fontFamily: 'var(--font-gotham)'
        }} data-id="element-192">
            <span className="font-bold text-white" data-id="element-193">{peerPercentage}%</span> of
            peers answered like you.
          </p>
          <div className="h-1.5 rounded-full bg-white/10 overflow-hidden" data-id="element-194">
            <motion.div className="h-full rounded-full" style={{
            background: isCorrect ? '#4ade80' : '#fbbf24'
          }} initial={{
            width: 0
          }} animate={{
            width: `${peerPercentage}%`
          }} transition={{
            duration: 1,
            delay: 0.6,
            ease: 'easeOut'
          }} data-id="element-195" />
          </div>
        </motion.div>

        {/* HERO STAT: 42% Reduction */}
        <motion.div className="flex flex-col items-center justify-center py-6 mb-4" initial={{
        opacity: 0,
        scale: 0.8
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.8,
        delay: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }} data-id="element-196">
          <div className="relative" data-id="element-197">
            {/* Glow effect behind the number */}
            <motion.div className="absolute inset-0 rounded-full blur-3xl" style={{
            background: 'rgba(231, 111, 36, 0.25)'
          }} initial={{
            opacity: 0,
            scale: 0.5
          }} animate={{
            opacity: 1,
            scale: 1.5
          }} transition={{
            duration: 1.5,
            delay: 1.2
          }} data-id="element-198" />
            <div className="relative flex items-baseline justify-center" data-id="element-199">
              <span className="text-7xl font-black text-white tabular-nums" style={{
              fontFamily: 'var(--font-gotham)',
              letterSpacing: '-0.03em'
            }} data-id="element-200">
                {displayCount}
              </span>
              <span className="text-4xl font-black text-[#E76F24] ml-1" style={{
              fontFamily: 'var(--font-gotham)'
            }} data-id="element-201">
                %
              </span>
            </div>
          </div>
          <motion.p className="text-white/80 text-base font-semibold mt-2 text-center tracking-wide uppercase" style={{
          fontFamily: 'var(--font-gotham)'
        }} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 1.4
        }} data-id="element-202">
            reduction in risk of death
          </motion.p>
          <motion.p className="text-white/40 text-xs mt-1 text-center" style={{
          fontFamily: 'var(--font-gotham)'
        }} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.6,
          delay: 1.8
        }} data-id="element-203">
            Median OS: NE vs 14.4 months
          </motion.p>
        </motion.div>

        {/* KM Visualization */}
        <motion.div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-4 mb-6 h-[220px] flex flex-col relative" initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.6,
        delay: 0.5
      }} data-id="element-204">
          <h3 className="text-white/60 text-xs font-medium uppercase tracking-wider mb-4 text-center" style={{
          fontFamily: 'var(--font-gotham)'
        }} data-id="element-205">
            Overall Survival Over Time
          </h3>

          <div className="flex-1 relative w-full h-full ml-4 mb-4" data-id="element-206">
            {/* Y Axis Label */}
            <div className="absolute -left-6 top-1/2 -translate-y-1/2 -rotate-90 text-white/40 text-[10px] tracking-widest uppercase whitespace-nowrap" style={{
            fontFamily: 'var(--font-gotham)'
          }} data-id="element-207">
              OS (%)
            </div>

            {/* X Axis Label */}
            <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-white/40 text-[10px] tracking-widest uppercase" style={{
            fontFamily: 'var(--font-gotham)'
          }} data-id="element-208">
              Time (Months)
            </div>

            {/* Chart Area */}
            <div className="absolute inset-0 border-l border-b border-white/20" data-id="element-209">
              {/* Grid Lines */}
              <div className="absolute top-1/4 w-full border-b border-white/5" data-id="element-210"></div>
              <div className="absolute top-2/4 w-full border-b border-white/5" data-id="element-211"></div>
              <div className="absolute top-3/4 w-full border-b border-white/5" data-id="element-212"></div>

              <div className="absolute left-1/4 h-full border-l border-white/5" data-id="element-213"></div>
              <div className="absolute left-2/4 h-full border-l border-white/5" data-id="element-214"></div>
              <div className="absolute left-3/4 h-full border-l border-white/5" data-id="element-215"></div>

              {/* SVG Curves */}
              <svg width="100%" height="100%" viewBox="0 0 400 300" preserveAspectRatio="none" className="overflow-visible" data-id="element-216">
                {/* Shaded Separation Area */}
                <motion.path d="M 100 80 L 200 120 L 300 140 L 400 150 L 400 250 L 300 220 L 200 160 L 100 80 Z" fill="rgba(231, 111, 36, 0.15)" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                duration: 1,
                delay: 2.5
              }} data-id="element-217" />

                {/* Comparator Line (R-GemOx) */}
                <motion.path d="M 0 0 L 50 20 L 100 80 L 150 130 L 200 160 L 250 190 L 300 220 L 350 240 L 400 250" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" initial={{
                pathLength: 0
              }} animate={{
                pathLength: 1
              }} transition={{
                duration: 2,
                ease: 'easeOut',
                delay: 0.5
              }} data-id="element-218" />

                {/* COLUMVI Line - Changed to White for better contrast */}
                <motion.path d="M 0 0 L 50 15 L 100 80 L 150 100 L 200 120 L 250 130 L 300 140 L 350 145 L 400 150" fill="none" stroke="#FFFFFF" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" initial={{
                pathLength: 0
              }} animate={{
                pathLength: 1
              }} transition={{
                duration: 2,
                ease: 'easeOut',
                delay: 0.5
              }} data-id="element-219" />

                {/* Separation Highlight Marker */}
                <motion.circle cx="200" cy="140" r="5" fill="#E76F24" initial={{
                opacity: 0,
                scale: 0
              }} animate={{
                opacity: 1,
                scale: 1
              }} transition={{
                duration: 0.5,
                delay: 2.8
              }} data-id="element-220" />
                <motion.line x1="200" y1="120" x2="200" y2="160" stroke="#E76F24" strokeWidth="2" strokeDasharray="4 4" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                duration: 0.5,
                delay: 2.8
              }} data-id="element-221" />
              </svg>

              {/* Legend */}
              <motion.div className="absolute top-2 right-2 bg-black/40 backdrop-blur-md border border-white/10 rounded p-2" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.5,
              delay: 3
            }} data-id="element-222">
                <div className="flex items-center mb-1" data-id="element-223">
                  <div className="w-3 h-1 bg-white mr-2 rounded" data-id="element-224"></div>
                  <span className="text-white text-[10px]" style={{
                  fontFamily: 'var(--font-gotham)'
                }} data-id="element-225">
                    COLUMVI + GemOx
                  </span>
                </div>
                <div className="flex items-center" data-id="element-226">
                  <div className="w-3 h-1 bg-white/30 mr-2 rounded" data-id="element-227"></div>
                  <span className="text-white/70 text-[10px]" style={{
                  fontFamily: 'var(--font-gotham)'
                }} data-id="element-228">
                    R-GemOx
                  </span>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Insight & Data */}
        <div className="flex-1 flex flex-col justify-end" data-id="element-229">
          <motion.h2 className="text-white text-xl leading-tight mb-3" style={{
          fontFamily: 'var(--font-gotham)',
          fontWeight: 700
        }} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.8
        }} data-id="element-230">
            Curative potential is reflected in sustained separation over time
          </motion.h2>

          <motion.div className="border-l-2 border-[#E76F24] pl-3 py-1 mb-4 bg-gradient-to-r from-[#E76F24]/10 to-transparent" initial={{
          opacity: 0,
          x: -10
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6,
          delay: 1
        }} data-id="element-231">
            <p className="text-white/90 text-sm leading-snug" style={{
            fontFamily: 'var(--font-gotham)'
          }} data-id="element-232">
              Indicates long-term disease control — not just early response
            </p>
          </motion.div>

          {/* Continue Button */}
          <motion.button onClick={onContinue} className="w-full py-3.5 rounded-xl text-white text-sm font-semibold transition-all duration-300 border border-white/30 hover:bg-white/10 hover:border-white/60 mb-2" style={{
          fontFamily: 'var(--font-gotham)'
        }} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 1.4
        }} data-id="element-233">
            Continue →
          </motion.button>

          <div className="text-center text-white/30 text-[10px]" style={{
          fontFamily: 'var(--font-gotham)'
        }} data-id="element-234">
            Source: STARGLO 3-year follow-up, Sales Aid p.10
          </div>
        </div>
      </div>
    </ColumviBackground>;
}